import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getMarketHours } from '../api/marketData'
import { setMarketCalendarDay, todayET } from '../lib/marketHours'
import { useAuthStore } from '../auth/useAuthStore'

/**
 * Fetches the authoritative equity market-hours calendar for today (Schwab
 * /markets) once per ET day and feeds it into marketHours.ts. This lets
 * isMarketOpen()/getSessionPhase() recognize holidays and half-days instead of
 * assuming a fixed Mon–Fri 09:30–16:00 session. Requires a Schwab attachment;
 * no-ops otherwise (the clock-based fallback stays in effect).
 *
 * Mounted once in ConnectedShell.
 */
export function useMarketCalendar(): void {
  const schwabAttached = useAuthStore((s) =>
    s.platforms.some((p) => p.platformId === 'schwab'),
  )
  const date = todayET()
  const { data } = useQuery({
    // Date in the key so the query re-runs after midnight ET (next render).
    queryKey: ['market-hours', date],
    queryFn: () => getMarketHours(date),
    enabled: schwabAttached,
    staleTime: 12 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  useEffect(() => {
    if (data?.equity) setMarketCalendarDay(data.date || date, data.equity)
  }, [data, date])
}
